import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { LayoutGrid, Plus, Users } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { Button, ErrorText, Input, Label, Modal } from "@/components/ui";
import { api } from "@/lib/api";
import { useSpaces } from "@/lib/spaces";

interface Blueprint {
  id: string;
  label: string;
  description: string;
}

export default function Spaces() {
  const spaces = useSpaces();
  const [creating, setCreating] = useState(false);
  return (
    <PageShell title="Spaces">
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-sm text-zinc-500">A space is a place for one kind of work, with its own data, home page and chats.</p>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus size={16} /> New space
        </Button>
      </div>
      {spaces.data?.length === 0 && <p className="text-sm text-zinc-500">No spaces yet.</p>}
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {spaces.data?.map((s) => (
          <li key={s.id}>
            <Link to={`/spaces/${s.id}`} className="flex h-full items-start gap-3 rounded-xl border border-zinc-200 bg-white p-4 hover:border-indigo-300 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-indigo-800">
              <LayoutGrid size={18} className="mt-0.5 shrink-0 text-indigo-500" />
              <span className="min-w-0">
                <span className="block truncate font-medium">{s.name}</span>
                {s.description && <span className="line-clamp-2 text-sm text-zinc-500">{s.description}</span>}
                {s.role === "member" && (
                  <span className="mt-1 flex items-center gap-1 text-xs text-zinc-500">
                    <Users size={12} /> Shared by {s.owner}
                  </span>
                )}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {creating && <NewSpace onClose={() => setCreating(false)} />}
    </PageShell>
  );
}

function NewSpace({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const blueprints = useQuery({ queryKey: ["space-blueprints"], queryFn: () => api.get<Blueprint[]>("/api/spaces/blueprints"), staleTime: Infinity });
  const [form, setForm] = useState({ name: "", description: "", blueprint: "blank" });
  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>("/api/spaces", form),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["spaces"] });
      qc.invalidateQueries({ queryKey: ["folders"] });
      navigate(`/spaces/${r.id}`);
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }
  return (
    <Modal open onClose={onClose} title="New space" wide>
      <form className="space-y-4" onSubmit={submit}>
        <div>
          <p className="mb-2 text-sm font-medium">Start from</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {blueprints.data?.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setForm({ ...form, blueprint: b.id, name: form.name || (b.id === "blank" ? "" : b.label) })}
                className={clsx(
                  "rounded-lg border p-3 text-left text-sm",
                  form.blueprint === b.id ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40" : "border-zinc-200 hover:border-zinc-300 dark:border-zinc-700",
                )}
                aria-pressed={form.blueprint === b.id}
              >
                <span className="block font-medium">{b.label}</span>
                <span className="text-xs text-zinc-500">{b.description}</span>
              </button>
            ))}
          </div>
        </div>
        <Label label="Name">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </Label>
        <Label label="Description" hint="Optional. What the space is for, in a line.">
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Label>
        <ErrorText error={create.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={create.isPending}>Create</Button>
        </div>
      </form>
    </Modal>
  );
}
